"use client";

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface TableColumn {
  key: string;
  label: string;
  type?: "text" | "number" | "date" | "link";
}

interface TableBlockData {
  columns: TableColumn[];
  rows: Array<Record<string, unknown>>;
  settings?: {
    pageSize?: number; // 0 / missing = no pagination
    searchable?: boolean; // show the search box
    sortable?: boolean; // clickable sort headers
  };
}

interface TableBodyProps {
  block?: TableBlockData | null;
}

type SortDir = "asc" | "desc";

/**
 * Table body for `contentType === 'table'` posts.
 *
 * An interactive, theme-aware data table whose features are driven by
 * the author's `settings`:
 *   - `searchable`        → a search box that filters across all columns
 *   - `sortable`          → clickable, type-aware sort headers
 *   - `pageSize > 0`      → client-side pagination with a page bar
 *
 * Cells are type-formatted (date → locale date, link → anchor, number →
 * grouped). Client component so the search / sort / paging stay snappy
 * without a round-trip; the row set is small (authored inline).
 */
export function TableBody({ block }: TableBodyProps) {
  const columns = useMemo<TableColumn[]>(
    () => (Array.isArray(block?.columns) ? block!.columns : []),
    [block],
  );
  const rows = useMemo<Array<Record<string, unknown>>>(
    () => (Array.isArray(block?.rows) ? block!.rows : []),
    [block],
  );

  const settings = block?.settings ?? {};
  const searchable = settings.searchable === true;
  const sortable = settings.sortable === true;
  const pageSize = Math.max(0, Math.floor(Number(settings.pageSize) || 0));

  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  // ── filter ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      columns.some((c) =>
        String(row[c.key] ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [rows, columns, query]);

  // ── sort (type-aware, empties last) ─────────────────────────────────
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const type = columns.find((c) => c.key === sortKey)?.type;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const ae = av === null || av === undefined || av === "";
      const be = bv === null || bv === undefined || bv === "";
      if (ae && be) return 0;
      if (ae) return 1; // empties always sink
      if (be) return -1;
      let cmp: number;
      if (type === "number") {
        cmp = Number(av) - Number(bv);
      } else if (type === "date") {
        cmp =
          new Date(String(av)).getTime() - new Date(String(bv)).getTime();
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return cmp * dir;
    });
  }, [filtered, sortKey, sortDir, columns]);

  // ── paginate ────────────────────────────────────────────────────────
  const total = sorted.length;
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    if (pageSize <= 0) return sorted;
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, pageSize, currentPage]);

  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setPage(1);
  }, [query, sortKey, sortDir]);

  if (!block || columns.length === 0) return null;

  const toggleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const from = total === 0 ? 0 : (currentPage - 1) * (pageSize || total) + 1;
  const to = pageSize > 0 ? Math.min(currentPage * pageSize, total) : total;

  return (
    <div data-post-body="table" className="space-y-3">
      {/* Toolbar — search + live count */}
      {(searchable || pageSize > 0) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {searchable ? (
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search table…"
                aria-label="Search table"
                className="w-full rounded-md border border-border bg-card py-2 pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
            </div>
          ) : (
            <span />
          )}
          <span className="text-xs text-muted-foreground tabular-nums">
            {total > 0 ? (
              <>
                Showing {from}–{to} of {total}
                {query ? " (filtered)" : ""}
              </>
            ) : (
              "No matching rows"
            )}
          </span>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-muted">
            <tr>
              {columns.map((col) => {
                const active = sortKey === col.key;
                // `aria-sort` applied via spread so jsx-a11y doesn't choke on
                // a dynamic value; only the active column advertises a sort.
                const sortAttr = active
                  ? {
                      "aria-sort": (sortDir === "asc"
                        ? "ascending"
                        : "descending") as React.AriaAttributes["aria-sort"],
                    }
                  : {};
                return (
                  <th
                    key={col.key}
                    scope="col"
                    {...sortAttr}
                    className="border-b border-border px-4 py-2.5 text-left font-semibold text-foreground"
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className="group inline-flex items-center gap-1.5 hover:text-primary"
                      >
                        <span>{col.label}</span>
                        {active ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5 text-primary" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5 text-primary" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-primary" />
                        )}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-sm text-muted-foreground"
                >
                  No data to display.
                </td>
              </tr>
            ) : (
              paged.map((row, ri) => (
                <tr
                  key={ri}
                  className="border-b border-border last:border-0 odd:bg-card even:bg-muted/30 transition-colors hover:bg-primary/5"
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-2.5 align-top text-foreground"
                    >
                      {renderCell(row[col.key], col.type)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination bar — only when authored pageSize splits the data */}
      {pageSize > 0 && totalPages > 1 && (
        <nav
          className="flex items-center justify-between gap-2"
          aria-label="Table pagination"
        >
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </button>

          <div className="flex items-center gap-1">
            {pageWindow(currentPage, totalPages).map((p, i) =>
              p === "…" ? (
                <span
                  key={`gap-${i}`}
                  className="px-2 text-sm text-muted-foreground"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p as number)}
                  aria-current={p === currentPage ? "page" : undefined}
                  className={
                    p === currentPage
                      ? "h-8 min-w-8 rounded-md bg-primary px-2 text-sm font-medium text-primary-foreground"
                      : "h-8 min-w-8 rounded-md border border-border bg-card px-2 text-sm text-foreground transition-colors hover:bg-muted"
                  }
                >
                  {p}
                </button>
              ),
            )}
          </div>

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}

/** Compact page list: 1 … 4 5 [6] 7 8 … 20 */
function pageWindow(current: number, total: number): Array<number | "…"> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | "…"> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}

function renderCell(
  value: unknown,
  type?: TableColumn["type"],
): React.ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "date") {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
  }
  if (type === "link" && typeof value === "string") {
    const isExternal = /^https?:\/\//i.test(value);
    return (
      <Link
        href={value}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className="text-primary hover:underline"
      >
        {value}
      </Link>
    );
  }
  if (type === "number" && typeof value === "number") {
    return value.toLocaleString();
  }
  return String(value);
}

export default TableBody;
